import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  OrderedList,
  ListItem,
  Divider
} from "@chakra-ui/react";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const TermsOfService: React.FC = () => {
  return (
    <Box minH="100vh" bg="gray.50">
      <SEOHead
        pagePath="/terms-of-service"
        fallbackTitle="Terms of Service"
        fallbackDescription="Terms and conditions for using One Group services"
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg="gray.800" color="white" py={12}>
        <Container maxW="container.xl">
          <VStack spacing={3} align="start">
            <Text fontSize="xs" color="gray.400" textTransform="uppercase" letterSpacing="widest">
              Legal Agreement
            </Text>
            <Heading size="2xl" fontWeight="bold">
              Terms of Service
            </Heading>
            <Text fontSize="md" color="gray.300">
              Please read these terms carefully before using our services
            </Text>
            <Text fontSize="sm" color="gray.400" mt={2}>
              Last Updated: November 2025
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Content Section */}
      <Container maxW="container.lg" py={16}>
        <Box bg="white" p={10} borderRadius="lg" shadow="sm">
          <VStack spacing={8} align="stretch">
            {/* Introduction */}
            <Box>
              <Text fontSize="md" color="gray.700" lineHeight="tall">
                <strong>[PLACEHOLDER CONTENT]</strong> These Terms of Service ("Terms") constitute a legally binding
                agreement between you and One Group Pty Ltd (ABN XX XXX XXX XXX) ("Company", "we", "us", or "our")
                regarding your use of our business management platform and related services (collectively, the "Services").
                By accessing or using our Services, you agree to be bound by these Terms.
              </Text>
            </Box>

            <Divider />

            {/* Numbered Sections */}
            <OrderedList spacing={8} styleType="decimal" fontSize="md" color="gray.700">

              {/* Section 1 */}
              <ListItem>
                <Heading size="md" mb={3} color="gray.800">Acceptance of Terms</Heading>
                <VStack align="start" spacing={3} pl={4}>
                  <Text>
                    <strong>[PLACEHOLDER]</strong> By creating an account or using our Services, you acknowledge that
                    you have read, understood, and agree to be bound by these Terms and our Privacy Policy.
                  </Text>
                  <Text fontSize="sm">
                    1.1. If you are entering into these Terms on behalf of a company or other legal entity, you represent
                    that you have the authority to bind such entity to these Terms.
                  </Text>
                  <Text fontSize="sm">
                    1.2. We reserve the right to modify these Terms at any time. Continued use of the Services after
                    modifications constitutes acceptance of the updated Terms.
                  </Text>
                  <Text fontSize="sm">
                    1.3. You must be at least 18 years old to use our Services.
                  </Text>
                </VStack>
              </ListItem>

              {/* Section 2 */}
              <ListItem>
                <Heading size="md" mb={3} color="gray.800">Description of Services</Heading>
                <VStack align="start" spacing={3} pl={4}>
                  <Text>
                    <strong>[PLACEHOLDER]</strong> One Group provides a comprehensive business management platform
                    including, but not limited to:
                  </Text>
                  <Text fontSize="sm">
                    2.1. Client relationship management (CRM) tools
                  </Text>
                  <Text fontSize="sm">
                    2.2. Project and task management capabilities
                  </Text>
                  <Text fontSize="sm">
                    2.3. Billing and invoicing functionality
                  </Text>
                  <Text fontSize="sm">
                    2.4. Communication tools (email, SMS, voice)
                  </Text>
                  <Text fontSize="sm">
                    2.5. Data storage and file management
                  </Text>
                  <Text fontSize="sm">
                    2.6. Additional features as may be added or modified from time to time
                  </Text>
                </VStack>
              </ListItem>

              {/* Section 3 */}
              <ListItem>
                <Heading size="md" mb={3} color="gray.800">User Accounts and Responsibilities</Heading>
                <VStack align="start" spacing={3} pl={4}>
                  <Text>
                    <strong>[PLACEHOLDER]</strong> Account creation and maintenance requirements:
                  </Text>
                  <Text fontSize="sm">
                    3.1. You must provide accurate, current, and complete information during registration and maintain
                    the accuracy of such information.
                  </Text>
                  <Text fontSize="sm">
                    3.2. You are responsible for maintaining the confidentiality of your account credentials and for all
                    activities that occur under your account.
                  </Text>
                  <Text fontSize="sm">
                    3.3. You must immediately notify us of any unauthorized use of your account or any other security breach.
                  </Text>
                  <Text fontSize="sm">
                    3.4. You may not share your account access with others or use another person's account without permission.
                  </Text>
                  <Text fontSize="sm">
                    3.5. You agree to use the Services only for lawful purposes and in accordance with these Terms.
                  </Text>
                </VStack>
              </ListItem>

              {/* Section 4 */}
              <ListItem>
                <Heading size="md" mb={3} color="gray.800">Subscription Plans and Payments</Heading>
                <VStack align="start" spacing={3} pl={4}>
                  <Text>
                    <strong>[PLACEHOLDER]</strong> Payment terms and billing procedures:
                  </Text>
                  <Text fontSize="sm">
                    4.1. Our Services are provided on a subscription basis with various pricing tiers (Foundation, Growth,
                    Professional, Communications, Intelligence, Enterprise, Dominance).
                  </Text>
                  <Text fontSize="sm">
                    4.2. Subscription fees are billed in advance on a monthly or annual basis and are non-refundable except
                    as required by law.
                  </Text>
                  <Text fontSize="sm">
                    4.3. You authorize us to charge your designated payment method for all fees incurred under your account.
                  </Text>
                  <Text fontSize="sm">
                    4.4. Failure to pay fees may result in suspension or termination of your account.
                  </Text>
                  <Text fontSize="sm">
                    4.5. We may change our pricing with 30 days' notice. Continued use after the notice period constitutes
                    acceptance of the new pricing.
                  </Text>
                  <Text fontSize="sm">
                    4.6. All fees are exclusive of applicable taxes, which you are responsible for paying.
                  </Text>
                </VStack>
              </ListItem>

              {/* Section 5 */}
              <ListItem>
                <Heading size="md" mb={3} color="gray.800">Intellectual Property Rights</Heading>
                <VStack align="start" spacing={3} pl={4}>
                  <Text>
                    <strong>[PLACEHOLDER]</strong> Ownership and licensing of intellectual property:
                  </Text>
                  <Text fontSize="sm">
                    5.1. The Services and all content, features, and functionality are owned by the Company and protected
                    by international copyright, trademark, patent, and other intellectual property laws.
                  </Text>
                  <Text fontSize="sm">
                    5.2. You retain ownership of all content you upload to the Services ("User Content").
                  </Text>
                  <Text fontSize="sm">
                    5.3. By uploading User Content, you grant us a worldwide, non-exclusive, royalty-free license to use,
                    store, and process such content to provide the Services.
                  </Text>
                  <Text fontSize="sm">
                    5.4. You represent and warrant that you own or have the necessary rights to all User Content and that
                    such content does not infringe any third-party rights.
                  </Text>
                  <Text fontSize="sm">
                    5.5. The Company name, logo, and product names are trademarks of One Group Pty Ltd. You may not use
                    these marks without our prior written permission.
                  </Text>
                </VStack>
              </ListItem>

              {/* Section 6 */}
              <ListItem>
                <Heading size="md" mb={3} color="gray.800">Limitation of Liability</Heading>
                <VStack align="start" spacing={3} pl={4}>
                  <Text>
                    <strong>[PLACEHOLDER]</strong> Limitations on our liability to you:
                  </Text>
                  <Text fontSize="sm">
                    6.1. TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE COMPANY SHALL NOT BE LIABLE FOR ANY INDIRECT,
                    INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES.
                  </Text>
                  <Text fontSize="sm">
                    6.2. OUR TOTAL LIABILITY FOR ALL CLAIMS ARISING FROM OR RELATING TO THE SERVICES SHALL NOT EXCEED
                    THE AMOUNT YOU PAID US IN THE 12 MONTHS PRECEDING THE CLAIM.
                  </Text>
                  <Text fontSize="sm">
                    6.3. THE SERVICES ARE PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.
                  </Text>
                  <Text fontSize="sm">
                    6.4. We do not warrant that the Services will be uninterrupted, error-free, or secure.
                  </Text>
                  <Text fontSize="sm">
                    6.5. Some jurisdictions do not allow the exclusion of certain warranties, so some of the above
                    exclusions may not apply to you.
                  </Text>
                </VStack>
              </ListItem>

              {/* Section 7 */}
              <ListItem>
                <Heading size="md" mb={3} color="gray.800">Termination</Heading>
                <VStack align="start" spacing={3} pl={4}>
                  <Text>
                    <strong>[PLACEHOLDER]</strong> Account termination procedures:
                  </Text>
                  <Text fontSize="sm">
                    7.1. You may terminate your account at any time by contacting our support team or through your
                    account settings.
                  </Text>
                  <Text fontSize="sm">
                    7.2. We may suspend or terminate your account immediately if you breach these Terms or engage in
                    fraudulent or illegal activity.
                  </Text>
                  <Text fontSize="sm">
                    7.3. Upon termination, your right to use the Services will immediately cease.
                  </Text>
                  <Text fontSize="sm">
                    7.4. We will make reasonable efforts to provide you with access to your data for 30 days after
                    termination, after which it may be permanently deleted.
                  </Text>
                  <Text fontSize="sm">
                    7.5. Termination does not relieve you of any payment obligations incurred before termination.
                  </Text>
                </VStack>
              </ListItem>

              {/* Section 8 */}
              <ListItem>
                <Heading size="md" mb={3} color="gray.800">Governing Law and Dispute Resolution</Heading>
                <VStack align="start" spacing={3} pl={4}>
                  <Text>
                    <strong>[PLACEHOLDER]</strong> Legal jurisdiction and dispute resolution process:
                  </Text>
                  <Text fontSize="sm">
                    8.1. These Terms shall be governed by and construed in accordance with the laws of Victoria, Australia,
                    without regard to its conflict of law provisions.
                  </Text>
                  <Text fontSize="sm">
                    8.2. Any disputes arising from these Terms or the Services shall be subject to the exclusive jurisdiction
                    of the courts of Victoria, Australia.
                  </Text>
                  <Text fontSize="sm">
                    8.3. Before initiating legal proceedings, you agree to first attempt to resolve any dispute through
                    our internal complaints process (see Complaints Policy).
                  </Text>
                  <Text fontSize="sm">
                    8.4. If the dispute cannot be resolved internally within 30 days, either party may refer the matter to
                    the Australian Financial Complaints Authority (AFCA) or equivalent dispute resolution service.
                  </Text>
                  <Text fontSize="sm">
                    8.5. Nothing in these Terms limits your rights under the Australian Consumer Law.
                  </Text>
                </VStack>
              </ListItem>

            </OrderedList>

            <Divider />

            {/* Additional Provisions */}
            <Box>
              <Heading size="md" mb={3} color="gray.800">Additional Provisions</Heading>
              <VStack align="start" spacing={2} fontSize="sm" color="gray.700">
                <Text>
                  <strong>Severability:</strong> If any provision of these Terms is found to be invalid or unenforceable,
                  the remaining provisions will continue in full force and effect.
                </Text>
                <Text>
                  <strong>Entire Agreement:</strong> These Terms, together with our Privacy Policy, constitute the entire
                  agreement between you and the Company regarding the Services.
                </Text>
                <Text>
                  <strong>Waiver:</strong> No waiver of any term of these Terms shall be deemed a further or continuing
                  waiver of such term or any other term.
                </Text>
                <Text>
                  <strong>Assignment:</strong> You may not assign or transfer these Terms without our prior written consent.
                  We may assign these Terms without restriction.
                </Text>
              </VStack>
            </Box>

            <Divider />

            {/* Contact Information */}
            <Box bg="gray.100" p={6} borderRadius="md">
              <Heading size="sm" mb={3}>Questions About These Terms?</Heading>
              <Text fontSize="sm" color="gray.700">
                <strong>[PLACEHOLDER]</strong> If you have any questions about these Terms of Service, please contact us at:
              </Text>
              <Text fontSize="sm" color="gray.700" mt={2}>
                <strong>Email:</strong> legal@onegroup.com.au<br />
                <strong>Phone:</strong> 1300 XXX XXX<br />
                <strong>Address:</strong> [Placeholder Address], Melbourne VIC 3000
              </Text>
            </Box>
          </VStack>
        </Box>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default TermsOfService;
